/**
 * Pull a user-facing message out of an axios/Laravel error response.
 *
 * Laravel 422 responses look like:
 *   { message: "The description field must not be greater than 1000 characters.",
 *     errors:  { description: ["The description field must not be greater than 1000 characters."] } }
 *
 * The top-level `message` on a 422 used to be the generic
 * "The given data was invalid." which is useless for end users — newer
 * Laravel uses the first field error there, but we still defensively prefer
 * the per-field map when it's present and contains more than one issue.
 *
 * Falls back through:
 *   1. transport failure (no response at all) — network / timeout
 *   2. errors map (joined with " · " when several fields fail)
 *   3. top-level message
 *   4. a message for statuses whose body is typically empty (429/413/5xx)
 *   5. supplied default
 *
 * This is the single extractor for the whole app — the React Query cache
 * handlers in lib/queryClient.js delegate here rather than keeping their own.
 *
 * @param {unknown}  err      axios error (or anything thrown)
 * @param {string}   fallback message shown if nothing useful is in the response
 * @returns {string}
 */
export function apiErrorMessage(err, fallback = 'Something went wrong') {
  const res = err?.response
  const data = res?.data

  // No response at all means the request never completed — DNS, offline,
  // CORS, or a timeout. err.message here is an axios internal string
  // ("Network Error", "timeout of 60000ms exceeded"), which is not worth
  // showing a clinician.
  if (!res) {
    return err?.code === 'ECONNABORTED'
      ? 'That took too long and timed out — please try again.'
      : 'Network error — check your connection.'
  }
  if (!data) return statusMessage(res.status) || err?.message || fallback

  const errors = data.errors
  if (errors && typeof errors === 'object') {
    const messages = []
    for (const field of Object.keys(errors)) {
      const list = errors[field]
      if (Array.isArray(list)) {
        for (const m of list) if (typeof m === 'string' && m) messages.push(m)
      } else if (typeof list === 'string' && list) {
        messages.push(list)
      }
    }
    if (messages.length) {
      // Cap at 3 so a form with many invalid fields doesn't overflow the toast.
      const shown = messages.slice(0, 3).join(' · ')
      return messages.length > 3 ? `${shown} (+${messages.length - 3} more)` : shown
    }
  }

  if (typeof data.message === 'string' && data.message) return data.message
  return statusMessage(res.status) || fallback
}

/**
 * Messages for statuses whose response body is typically empty or unhelpful.
 * Returns null when the status has nothing special to say, so callers fall
 * through to their own default.
 *
 * @param {number|undefined} status
 * @returns {string|null}
 */
function statusMessage(status) {
  if (status === 429) return 'Too many requests — slow down a moment and try again.'
  if (status === 413) return 'That file is too large to upload.'
  if (status >= 500) return 'The server had a problem handling that. Please try again.'
  return null
}
